import {
  CATEGORIAS_DE_CNH,
  criarReservaSchema,
  formatarMoedaDoWire,
  type CriarReservaEntrada,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { errorMessage } from '@/lib/api'

import { useCriarReserva } from './api'

/**
 * FECHAR A RESERVA a partir de uma cotação já na tela — a reserva assistida
 * (Gap 13).
 *
 * ===========================================================================
 * POR QUE ELE NÃO COTA
 * ===========================================================================
 * Porque a cotação JÁ ESTÁ na tela atrás dele. O atendente está no simulador,
 * com o cliente no telefone, olhando o total daquela categoria — pedir para ele
 * escolher categoria e período de novo, num diálogo, seria perguntar duas vezes
 * a mesma coisa e abrir a porta para o clássico "cotei X, reservei Y".
 *
 * A reserva NASCE da cotação congelada: categoria, período, locais, extras e
 * preço vêm todos do `quoteId`. Este formulário coleta a única coisa que a
 * cotação não tem — QUEM vai dirigir.
 *
 * ===========================================================================
 * A CHAVE DE IDEMPOTÊNCIA NASCE COM O DIÁLOGO, NÃO COM O CLIQUE
 * ===========================================================================
 * Dois cliques no botão, ou um timeout com retry, são a MESMA intenção e devem
 * devolver a MESMA reserva (invariante nº 9). Chave gerada no clique daria duas
 * reservas para um duplo clique — que é o gesto mais comum que existe em botão
 * que demora. Ela é derivada do `quoteId`: cotação nova, intenção nova.
 */

export interface CotacaoParaFechar {
  readonly quoteId: string
  readonly accountId: string
  readonly categoriaNome: string
  readonly total: string
  readonly caucao: string
  readonly diarias: number
}

interface Props {
  readonly cotacao: CotacaoParaFechar | null
  readonly aoFechar: () => void
  readonly aoCriar: (reservaId: string) => void
}

const CONDUTOR_VAZIO = {
  nome: '',
  cpf: '',
  dataDeNascimento: '',
  telefone: '',
  cnhNumero: '',
  cnhCategoria: 'B',
  cnhValidade: '',
  cnhPrimeiraHabilitacao: '',
}

export function DialogoDeFecharReserva({ cotacao, aoFechar, aoCriar }: Props): ReactElement {
  const [condutor, setCondutor] = useState(CONDUTOR_VAZIO)
  const criar = useCriarReserva()

  const quoteId = cotacao?.quoteId
  // Ver o cabeçalho: a chave acompanha a COTAÇÃO, não o clique.
  const chaveDeIdempotencia = useMemo(
    () => crypto.randomUUID(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a chave acompanha a cotação
    [quoteId],
  )

  function fechar(): void {
    setCondutor(CONDUTOR_VAZIO)
    aoFechar()
  }

  function confirmar(): void {
    if (cotacao === null) {
      return
    }

    const corpo: CriarReservaEntrada = {
      quoteId: cotacao.quoteId,
      accountId: cotacao.accountId,
      condutores: [
        {
          nome: condutor.nome,
          cpf: condutor.cpf,
          dataDeNascimento: condutor.dataDeNascimento,
          ...(condutor.telefone === '' ? {} : { telefone: condutor.telefone }),
          cnhNumero: condutor.cnhNumero,
          cnhCategoria: condutor.cnhCategoria as (typeof CATEGORIAS_DE_CNH)[number],
          cnhValidade: condutor.cnhValidade,
          cnhPrimeiraHabilitacao: condutor.cnhPrimeiraHabilitacao,
          principal: true,
        },
      ],
    }

    // O MESMO schema que a API valida — o erro aparece antes da ida à rede.
    const validado = criarReservaSchema.safeParse(corpo)
    if (!validado.success) {
      toast.error(validado.error.issues[0]?.message ?? 'Confira os dados do condutor.')
      return
    }

    criar.mutate(
      { corpo, chaveDeIdempotencia },
      {
        onSuccess: (reserva) => {
          toast.success(`Reserva nº ${String(reserva.numero)} criada em rascunho.`)
          fechar()
          aoCriar(reserva.id)
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  function campo(nome: keyof typeof condutor) {
    return (evento: { target: { value: string } }) => {
      setCondutor((atual) => ({ ...atual, [nome]: evento.target.value }))
    }
  }

  return (
    <Dialog
      open={cotacao !== null}
      onOpenChange={(abrir) => {
        if (!abrir) {
          fechar()
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fechar a reserva</DialogTitle>
          <DialogDescription>
            Falta só quem vai dirigir. Categoria, período e preço já vêm congelados da cotação.
          </DialogDescription>
        </DialogHeader>

        {cotacao === null ? null : (
          <Alert>
            <AlertTitle>
              {cotacao.categoriaNome} · {cotacao.diarias}{' '}
              {cotacao.diarias === 1 ? 'diária' : 'diárias'} · {formatarMoedaDoWire(cotacao.total)}
            </AlertTitle>
            <AlertDescription>
              Caução de {formatarMoedaDoWire(cotacao.caucao)}. Mudar o período é cotar de novo.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Nome do condutor principal" obrigatorio className="sm:col-span-2">
            {(controle) => (
              <Input id={controle.id} value={condutor.nome} onChange={campo('nome')} />
            )}
          </Campo>

          <Campo rotulo="CPF" obrigatorio>
            {(controle) => (
              <Input
                id={controle.id}
                inputMode="numeric"
                placeholder="Só números"
                value={condutor.cpf}
                onChange={campo('cpf')}
              />
            )}
          </Campo>

          <Campo rotulo="Nascimento" obrigatorio>
            {(controle) => (
              <Input
                id={controle.id}
                type="date"
                value={condutor.dataDeNascimento}
                onChange={campo('dataDeNascimento')}
              />
            )}
          </Campo>

          <Campo rotulo="Nº da CNH" obrigatorio>
            {(controle) => (
              <Input
                id={controle.id}
                inputMode="numeric"
                placeholder="11 dígitos"
                value={condutor.cnhNumero}
                onChange={campo('cnhNumero')}
              />
            )}
          </Campo>

          <Campo rotulo="Categoria da CNH" obrigatorio>
            {(controle) => (
              <Select
                id={controle.id}
                value={condutor.cnhCategoria}
                onChange={campo('cnhCategoria')}
              >
                {CATEGORIAS_DE_CNH.map((categoria) => (
                  <option key={categoria} value={categoria}>
                    {categoria}
                  </option>
                ))}
              </Select>
            )}
          </Campo>

          <Campo rotulo="Validade da CNH" obrigatorio>
            {(controle) => (
              <Input
                id={controle.id}
                type="date"
                value={condutor.cnhValidade}
                onChange={campo('cnhValidade')}
              />
            )}
          </Campo>

          <Campo
            rotulo="Primeira habilitação"
            obrigatorio
            dica="É daqui que sai o tempo de CNH que a categoria exige."
          >
            {(controle) => (
              <Input
                id={controle.id}
                type="date"
                value={condutor.cnhPrimeiraHabilitacao}
                onChange={campo('cnhPrimeiraHabilitacao')}
              />
            )}
          </Campo>

          <Campo rotulo="Telefone">
            {(controle) => (
              <Input
                id={controle.id}
                inputMode="tel"
                value={condutor.telefone}
                onChange={campo('telefone')}
              />
            )}
          </Campo>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={fechar}>
            Cancelar
          </Button>
          <Button onClick={confirmar} disabled={criar.isPending}>
            {criar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Criar reserva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
